import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { MODE_LABEL } from "@/components/groups/GroupCard";
import { LocationFields } from "@/components/forms/AddressFields";
import { MultiSelect } from "@/components/directory/MultiSelect";
import { ImageUploader, CoverUploader } from "@/components/forms/MediaUploader";
import { RichTextEditor, FULL_TOOLBAR } from "@/components/forms/RichTextEditor";

// Mirrors the server caps (`services/group_models`), surfaced through /api/groups/config.
const NAME_MAX = 100;
const SHORT_DESC_MAX = 300;

/**
 * Lightweight Group creation (GRP-DEC-023): name, handle, short description, membership
 * type, Listed/Unlisted, category. Everything else is completed later in Group settings.
 * Saving as a Draft does NOT reserve the handle (GRP-DEC-279) — it is claimed at publish.
 */
export function CreateGroupDialog({ open, onOpenChange, onCreated }) {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [form, setForm] = React.useState({
    display_name: "", handle: "", short_description: "",
    membership_mode: "open", listed: true, category: "",
    business_classifications: [], location: {}, is_online: false,
    about: "", profile_image_url: null, cover_url: null,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: categories } = useQuery({
    queryKey: ["ref-group-categories"],
    queryFn: async () => (await api.get("/reference/group_categories")).data,
    staleTime: 10 * 60 * 1000,
  });
  // The SAME classification reference data a Business profile uses (GRP-DEC-286).
  const { data: classifications } = useQuery({
    queryKey: ["ref-business-classifications"],
    queryFn: async () => (await api.get("/reference/business_classifications")).data,
    staleTime: 10 * 60 * 1000,
  });

  const submit = async (publish) => {
    if (!form.display_name.trim()) return toast.error("Give your group a name.");
    if (publish && !form.handle.trim()) return toast.error("Choose a handle to publish.");
    setBusy(true);
    try {
      const { data } = await api.post("/groups", { ...form, publish });
      toast.success(publish ? "Group published." : "Draft saved.");
      onOpenChange(false);
      setForm({ display_name: "", handle: "", short_description: "",
        membership_mode: "open", listed: true, category: "",
        business_classifications: [], location: {}, is_online: false,
        about: "", profile_image_url: null, cover_url: null });
      onCreated?.(data.group);
      if (publish && data.group?.url) navigate(data.group.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create that group.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] max-w-lg overflow-y-auto" data-testid="create-group-dialog">
        <DialogHeader>
          <DialogTitle>Create a group</DialogTitle>
          <DialogDescription>
            Name it, give it a handle and add the identity details — you can refine
            everything later in Group settings.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cg-name">Group name ({form.display_name.length}/{NAME_MAX})</Label>
            <Input id="cg-name" value={form.display_name} maxLength={NAME_MAX}
                   onChange={(e) => set("display_name", e.target.value)}
                   placeholder="Colorado Quantum" data-testid="create-group-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cg-handle">Handle</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">/g/</span>
              <Input id="cg-handle" value={form.handle} maxLength={50}
                     onChange={(e) => set("handle", e.target.value.replace(/[^A-Za-z0-9-]/g, ""))}
                     placeholder="colorado-quantum" data-testid="create-group-handle" />
            </div>
            <p className="text-xs text-muted-foreground">
              Handles are unique across Startup Otter and are only reserved when you publish.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cg-desc">
              Short description ({form.short_description.length}/{SHORT_DESC_MAX})
            </Label>
            <Textarea id="cg-desc" value={form.short_description} maxLength={SHORT_DESC_MAX}
                      rows={2}
                      onChange={(e) => set("short_description", e.target.value)}
                      data-testid="create-group-description" />
          </div>
          {/* Group identity image + banner — the SAME upload controls (and therefore the
              same file-type, size, validation, authorization and storage rules) a
              Business / Capital logo and 4:1 banner use. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <ImageUploader value={form.profile_image_url}
                           onChange={(u) => set("profile_image_url", u)}
                           label="Thumbnail / profile image (square)"
                           testId="create-group-thumbnail" />
            <div>
              <Label className="mb-1.5 block">Banner</Label>
              <CoverUploader value={form.cover_url} onChange={(u) => set("cover_url", u)}
                             aspect={4} testId="create-group-banner" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>About</Label>
            <RichTextEditor value={form.about} onChange={(v) => set("about", v)}
                            toolbar={FULL_TOOLBAR}
                            placeholder="What this group is for…"
                            testId="create-group-about" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Membership</Label>
              <Select value={form.membership_mode}
                      onValueChange={(v) => set("membership_mode", v)}>
                <SelectTrigger data-testid="create-group-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MODE_LABEL).map(([v, label]) => (
                    <SelectItem key={v} value={v}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="create-group-category">
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {(categories || []).map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Classification</Label>
            <MultiSelect label="Any classification" options={classifications || []}
                         selected={form.business_classifications}
                         onChange={(arr) => set("business_classifications", arr)}
                         testId="create-group-classifications" />
          </div>
          {/* Canonical platform location fieldset. OPTIONAL and independent of "Online
              group": a group can be online AND based in Denver, Colorado. */}
          <div className="space-y-1.5">
            <Label>Location (optional)</Label>
            <LocationFields value={form.location} testIdPrefix="create-group-location"
                            onChange={(l) => set("location", l)} />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Online group</p>
              <p className="text-xs text-muted-foreground">
                This group meets or connects online. It can still have a location.
              </p>
            </div>
            <Switch checked={form.is_online} onCheckedChange={(v) => set("is_online", v)}
                    data-testid="create-group-online" />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Listed in discovery</p>
              <p className="text-xs text-muted-foreground">
                Unlisted groups are reachable only by direct link or invitation.
              </p>
            </div>
            <Switch checked={form.listed} onCheckedChange={(v) => set("listed", v)}
                    data-testid="create-group-listed" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => submit(false)} disabled={busy}
                  data-testid="create-group-draft">
            Save draft
          </Button>
          <Button onClick={() => submit(true)} disabled={busy}
                  data-testid="create-group-publish">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Publish group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
